import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Container,
    Divider,
    Grid,
    IconButton,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableFooter,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import axios from 'axios';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material/styles';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import LastPageIcon from '@mui/icons-material/LastPage';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { toast } from 'react-toastify';

const formatCurrency = (value) => {
    const safeValue = Number(value || 0);
    return new Intl.NumberFormat('en-LK', {
        style: 'currency',
        currency: 'LKR',
        maximumFractionDigits: 0,
    }).format(safeValue);
};

const downloadCsv = (filename, columns, rows) => {
    const header = columns.map((column) => `"${column.label}"`).join(',');
    const dataLines = rows.map((row) =>
        columns
            .map((column) => {
                const value = row[column.key] ?? '';
                const safeValue = String(value).replace(/"/g, '""');
                return `"${safeValue}"`;
            })
            .join(',')
    );
    const csvContent = [header, ...dataLines].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', `${filename}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

function TablePaginationActions(props) {
    const theme = useTheme();
    const { count, page, rowsPerPage, onPageChange } = props;

    const handleFirstPageButtonClick = (event) => {
        onPageChange(event, 0);
    };

    const handleBackButtonClick = (event) => {
        onPageChange(event, page - 1);
    };

    const handleNextButtonClick = (event) => {
        onPageChange(event, page + 1);
    };

    const handleLastPageButtonClick = (event) => {
        onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
    };

    return (
        <Box sx={{ flexShrink: 0, ml: 2.5 }}>
            <IconButton
                onClick={handleFirstPageButtonClick}
                disabled={page === 0}
                aria-label="first page"
            >
                {theme.direction === 'rtl' ? <LastPageIcon /> : <FirstPageIcon />}
            </IconButton>
            <IconButton
                onClick={handleBackButtonClick}
                disabled={page === 0}
                aria-label="previous page"
            >
                {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
            </IconButton>
            <IconButton
                onClick={handleNextButtonClick}
                disabled={page >= Math.ceil(count / rowsPerPage) - 1}
                aria-label="next page"
            >
                {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
            </IconButton>
            <IconButton
                onClick={handleLastPageButtonClick}
                disabled={page >= Math.ceil(count / rowsPerPage) - 1}
                aria-label="last page"
            >
                {theme.direction === 'rtl' ? <FirstPageIcon /> : <LastPageIcon />}
            </IconButton>
        </Box>
    );
}

TablePaginationActions.propTypes = {
    count: PropTypes.number.isRequired,
    onPageChange: PropTypes.func.isRequired,
    page: PropTypes.number.isRequired,
    rowsPerPage: PropTypes.number.isRequired,
};

function ShopSalesReport() {
    const [filters, setFilters] = useState({
        fromDate: new Date().toISOString().substring(0, 10),
        toDate: new Date().toISOString().substring(0, 10),
        shop: 'All',
    });
    const [loading, setLoading] = useState(false);
    const [searchDetails, setSearchDetails] = useState('');
    const [searchCashier, setSearchCashier] = useState('');
    const [pageDetails, setPageDetails] = useState(0);
    const [rowsPerPageDetails, setRowsPerPageDetails] = useState(10);
    const [pageCashier, setPageCashier] = useState(0);
    const [rowsPerPageCashier, setRowsPerPageCashier] = useState(5);
    const [reportData, setReportData] = useState({
        range: filters,
        summary: {
            invoiceCount: 0,
            grossSales: 0,
            totalDiscount: 0,
            totalSales: 0,
        },
        shopSummary: [],
        invoiceDetails: [],
        cashierSummary: [],
    });

    const summaryCards = [
        {
            label: 'Gross Sales',
            value: formatCurrency(reportData.summary.grossSales),
            tone: 'linear-gradient(135deg, #E6F5F4 0%, #F7FFFE 100%)',
        },
        {
            label: 'Discounts',
            value: formatCurrency(reportData.summary.totalDiscount),
            tone: 'linear-gradient(135deg, #F9EAE1 0%, #FFF5EF 100%)',
        },
        {
            label: 'Net Sales',
            value: formatCurrency(reportData.summary.totalSales),
            tone: 'linear-gradient(135deg, #E4F2FF 0%, #F4FAFF 100%)',
        },
        {
            label: 'Invoices',
            value: reportData.summary.invoiceCount || 0,
            tone: 'linear-gradient(135deg, #F6E8FF 0%, #FBF5FF 100%)',
        },
    ];

    const filteredDetails = useMemo(() => {
        const term = searchDetails.trim().toLowerCase();
        if (!term) {
            return reportData.invoiceDetails;
        }
        return reportData.invoiceDetails.filter((row) => {
            const haystack = [
                row.invoiceNumber,
                row.invoiceDate,
                row.shop,
                row.cashier,
            ]
                .filter(Boolean)
                .join(' ')
                .toLowerCase();
            return haystack.includes(term);
        });
    }, [reportData.invoiceDetails, searchDetails]);

    const filteredCashiers = useMemo(() => {
        const term = searchCashier.trim().toLowerCase();
        if (!term) {
            return reportData.cashierSummary;
        }
        return reportData.cashierSummary.filter((row) => {
            const name = row.cashier ? String(row.cashier).toLowerCase() : '';
            return name.includes(term);
        });
    }, [reportData.cashierSummary, searchCashier]);

    const pagedDetails =
        rowsPerPageDetails > 0
            ? filteredDetails.slice(
                pageDetails * rowsPerPageDetails,
                pageDetails * rowsPerPageDetails + rowsPerPageDetails
            )
            : filteredDetails;

    const pagedCashiers =
        rowsPerPageCashier > 0
            ? filteredCashiers.slice(
                pageCashier * rowsPerPageCashier,
                pageCashier * rowsPerPageCashier + rowsPerPageCashier
            )
            : filteredCashiers;

    const downloadShopSummary = () => {
        const rows = reportData.shopSummary.map((row) => ({
            shop: row.shop || 'Unknown',
            invoiceCount: row.invoiceCount,
            grossSales: row.grossSales,
            totalDiscount: row.totalDiscount,
            totalSales: row.totalSales,
        }));
        downloadCsv(
            'shop-sales-summary',
            [
                { label: 'Branch', key: 'shop' },
                { label: 'Invoices', key: 'invoiceCount' },
                { label: 'Gross Sales', key: 'grossSales' },
                { label: 'Discounts', key: 'totalDiscount' },
                { label: 'Net Sales', key: 'totalSales' },
            ],
            rows
        );
    };

    const downloadInvoiceDetails = () => {
        const rows = filteredDetails.map((row) => ({
            invoiceNumber: row.invoiceNumber,
            invoiceDate: row.invoiceDate,
            shop: row.shop,
            cashier: row.cashier,
            grossTotal: row.grossTotal,
            discount: row.discount,
            netTotal: row.netTotal,
        }));
        downloadCsv(
            'shop-sales-invoices',
            [
                { label: 'Invoice', key: 'invoiceNumber' },
                { label: 'Date', key: 'invoiceDate' },
                { label: 'Branch', key: 'shop' },
                { label: 'Cashier', key: 'cashier' },
                { label: 'Gross', key: 'grossTotal' },
                { label: 'Discount', key: 'discount' },
                { label: 'Net', key: 'netTotal' },
            ],
            rows
        );
    };

    const downloadCashierSummary = () => {
        const rows = filteredCashiers.map((row) => ({
            cashier: row.cashier || 'Unknown',
            invoiceCount: row.invoiceCount,
            totalSales: row.totalSales,
        }));
        downloadCsv(
            'cashier-sales-summary',
            [
                { label: 'Cashier', key: 'cashier' },
                { label: 'Invoices', key: 'invoiceCount' },
                { label: 'Net Sales', key: 'totalSales' },
            ],
            rows
        );
    };

    const handleChange = (event) => {
        const { name, value } = event.target;
        setFilters((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const fetchReport = async () => {
        setLoading(true);
        try {
            const response = await axios.get('https://serverfourpaws.ccsoftwares.com/reports/shop-sales', {
                params: {
                    fromDate: filters.fromDate,
                    toDate: filters.toDate,
                    shop: filters.shop === 'All' ? null : filters.shop,
                },
            });
            setReportData(response.data);
            setPageDetails(0);
            setPageCashier(0);
            if (!response.data.invoiceDetails.length) {
                toast.info('No records found for the selected range.');
            }
        } catch (error) {
            toast.error('Unable to load report data.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReport();
    }, []);

    return (
        <Container maxWidth={false}>
            <Box mt={0}>
                <Card sx={{ padding: '15px', mb: 3 }}>
                    <CardHeader
                        title="Shop Sales Report"
                        titleTypographyProps={{ fontFamily: 'Oswald, sans-serif', fontWeight: 'bold' }}
                    />
                    <Divider />
                    <br />
                    <Grid container spacing={2}>
                        <Grid item md={3} xs={12}>
                            <TextField
                                fullWidth
                                label="From Date"
                                name="fromDate"
                                type="date"
                                value={filters.fromDate}
                                onChange={handleChange}
                                variant="outlined"
                                size="small"
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ max: filters.toDate }}
                            />
                        </Grid>
                        <Grid item md={3} xs={12}>
                            <TextField
                                fullWidth
                                label="To Date"
                                name="toDate"
                                type="date"
                                value={filters.toDate}
                                onChange={handleChange}
                                variant="outlined"
                                size="small"
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ min: filters.fromDate }}
                            />
                        </Grid>
                        <Grid item md={3} xs={12}>
                            <TextField
                                select
                                fullWidth
                                label="Branch"
                                name="shop"
                                value={filters.shop}
                                onChange={handleChange}
                                variant="outlined"
                                size="small"
                            >
                                <MenuItem value="All">All Branches</MenuItem>
                                <MenuItem value="Ratnapura">Ratnapura</MenuItem>
                                <MenuItem value="Kalawana">Kalawana</MenuItem>
                                <MenuItem value="Pelmadulla">Pelmadulla</MenuItem>
                            </TextField>
                        </Grid>
                        <Grid item md={3} xs={12} sx={{ display: 'flex', alignItems: 'center' }}>
                            <Button
                                variant="contained"
                                sx={{ width: '100%' }}
                                onClick={fetchReport}
                                disabled={loading}
                            >
                                {loading ? 'Loading...' : 'Search'}
                            </Button>
                        </Grid>
                    </Grid>
                </Card>

                <Grid container spacing={2} sx={{ mb: 3 }}>
                    {summaryCards.map((card) => (
                        <Grid item xs={12} sm={6} md={4} key={card.label}>
                            <Card sx={{ background: card.tone, boxShadow: '0 10px 20px rgba(0,0,0,0.06)' }}>
                                <CardContent>
                                    <Typography
                                        sx={{
                                            fontSize: '13px',
                                            textTransform: 'uppercase',
                                            fontWeight: 600,
                                            letterSpacing: '0.8px',
                                        }}
                                    >
                                        {card.label}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: '26px',
                                            fontWeight: 700,
                                            mt: 1,
                                            fontFamily: 'Oswald, sans-serif',
                                        }}
                                    >
                                        {card.value}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>

                <Card sx={{ padding: '15px', mb: 3 }}>
                    <CardHeader
                        title="Shop Summary"
                        titleTypographyProps={{ fontFamily: 'Oswald, sans-serif', fontWeight: 'bold' }}
                        action={
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<FileDownloadOutlinedIcon />}
                                onClick={downloadShopSummary}
                                disabled={!reportData.shopSummary.length}
                            >
                                CSV
                            </Button>
                        }
                    />
                    <Divider />
                    <TableContainer sx={{ mt: 2 }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Branch</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Invoices</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Gross Sales</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Discounts</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Net Sales</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {reportData.shopSummary.length ? (
                                    reportData.shopSummary.map((row) => (
                                        <TableRow key={row.shop}>
                                            <TableCell>{row.shop || 'Unknown'}</TableCell>
                                            <TableCell>{row.invoiceCount}</TableCell>
                                            <TableCell>{formatCurrency(row.grossSales)}</TableCell>
                                            <TableCell>{formatCurrency(row.totalDiscount)}</TableCell>
                                            <TableCell>{formatCurrency(row.totalSales)}</TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center">
                                            No shop summary data available.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Card>

                <Card sx={{ padding: '15px', mb: 3 }}>
                    <CardHeader
                        title="Invoice Details"
                        titleTypographyProps={{ fontFamily: 'Oswald, sans-serif', fontWeight: 'bold' }}
                        action={
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<FileDownloadOutlinedIcon />}
                                onClick={downloadInvoiceDetails}
                                disabled={!filteredDetails.length}
                            >
                                CSV
                            </Button>
                        }
                    />
                    <Divider />
                    <Box sx={{ mt: 2, mb: 2 }}>
                        <TextField
                            fullWidth
                            label="Search invoice, cashier, or branch"
                            value={searchDetails}
                            onChange={(event) => setSearchDetails(event.target.value)}
                            size="small"
                        />
                    </Box>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>#</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Invoice</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Branch</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Cashier</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Gross</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Discount</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Net</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {pagedDetails.length ? (
                                    pagedDetails.map((row, index) => (
                                        <TableRow key={`${row.invoiceNumber}-${index}`}>
                                            <TableCell>
                                                {pageDetails * rowsPerPageDetails + index + 1}
                                            </TableCell>
                                            <TableCell>{row.invoiceNumber}</TableCell>
                                            <TableCell>{row.invoiceDate}</TableCell>
                                            <TableCell>{row.shop}</TableCell>
                                            <TableCell>{row.cashier}</TableCell>
                                            <TableCell>{formatCurrency(row.grossTotal)}</TableCell>
                                            <TableCell>{formatCurrency(row.discount)}</TableCell>
                                            <TableCell>{formatCurrency(row.netTotal)}</TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center">
                                            No invoice details available.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                            <TableFooter>
                                <TableRow>
                                    <TablePagination
                                        rowsPerPageOptions={[5, 10, 25, { label: 'All', value: -1 }]}
                                        colSpan={8}
                                        count={filteredDetails.length}
                                        rowsPerPage={rowsPerPageDetails}
                                        page={pageDetails}
                                        slotProps={{
                                            select: {
                                                inputProps: {
                                                    'aria-label': 'rows per page',
                                                },
                                                native: true,
                                            },
                                        }}
                                        onPageChange={(event, newPage) => setPageDetails(newPage)}
                                        onRowsPerPageChange={(event) => {
                                            setRowsPerPageDetails(parseInt(event.target.value, 10));
                                            setPageDetails(0);
                                        }}
                                        ActionsComponent={TablePaginationActions}
                                    />
                                </TableRow>
                            </TableFooter>
                        </Table>
                    </TableContainer>
                </Card>

                <Card sx={{ padding: '15px' }}>
                    <CardHeader
                        title="Cashier Sales Summary"
                        titleTypographyProps={{ fontFamily: 'Oswald, sans-serif', fontWeight: 'bold' }}
                        action={
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<FileDownloadOutlinedIcon />}
                                onClick={downloadCashierSummary}
                                disabled={!filteredCashiers.length}
                            >
                                CSV
                            </Button>
                        }
                    />
                    <Divider />
                    <Box sx={{ mt: 2, mb: 2 }}>
                        <TextField
                            fullWidth
                            label="Search cashier"
                            value={searchCashier}
                            onChange={(event) => setSearchCashier(event.target.value)}
                            size="small"
                        />
                    </Box>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Cashier</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Invoices</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Net Sales</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {pagedCashiers.length ? (
                                    pagedCashiers.map((row) => (
                                        <TableRow key={row.cashier || 'unknown'}>
                                            <TableCell>{row.cashier || 'Unknown'}</TableCell>
                                            <TableCell>{row.invoiceCount}</TableCell>
                                            <TableCell>{formatCurrency(row.totalSales)}</TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={3} align="center">
                                            No cashier summary data available.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                            <TableFooter>
                                <TableRow>
                                    <TablePagination
                                        rowsPerPageOptions={[5, 10, 25, { label: 'All', value: -1 }]}
                                        colSpan={3}
                                        count={filteredCashiers.length}
                                        rowsPerPage={rowsPerPageCashier}
                                        page={pageCashier}
                                        slotProps={{
                                            select: {
                                                inputProps: {
                                                    'aria-label': 'rows per page',
                                                },
                                                native: true,
                                            },
                                        }}
                                        onPageChange={(event, newPage) => setPageCashier(newPage)}
                                        onRowsPerPageChange={(event) => {
                                            setRowsPerPageCashier(parseInt(event.target.value, 10));
                                            setPageCashier(0);
                                        }}
                                        ActionsComponent={TablePaginationActions}
                                    />
                                </TableRow>
                            </TableFooter>
                        </Table>
                    </TableContainer>
                </Card>
            </Box>
        </Container>
    );
}

export default ShopSalesReport;
